/**
 * Pure (no React) ChartSpec type + validation, shared by the renderer
 * (`QuestionChart`) and the generation validator (`math-validator-run`).
 *
 * Keeping this framework-free means the Node-side validator can confirm a
 * model-written chart spec is renderable without importing recharts/React.
 */

export const CHART_TYPES = ['line', 'bar', 'scatter', 'histogram'] as const
export type ChartType = (typeof CHART_TYPES)[number]

export interface ChartSpec {
  type: ChartType
  title?: string
  xAxis: { label: string; key: string }
  yAxis: { label: string; key: string }
  data: Record<string, unknown>[]
  series: { key: string; name: string; color: string }[]
  /**
   * Scatter-only: the categorical row field that splits points into distinctly
   * colored groups (e.g. "industry" → tech vs manufacturing), shown in a legend.
   * Optional — the renderer auto-detects a grouping field when this is unset (a
   * non-axis, all-string column with 2+ distinct values), so already-generated
   * "grouped scatter" specs render correctly without a data migration.
   */
  groupBy?: string
}

export type ChartSeries = { key: string; name: string; color: string }

/**
 * Coerce a possibly-malformed banked chart spec's `series` into a safe list:
 * a missing array → [], a series missing `name` → its key. Prevents the
 * series.map-of-undefined crash (4 items rendered BLANK in prod) and blank
 * legends (95 items) found in the 2026-06-26 bank audit.
 */
export function normalizeChartSeries(spec: ChartSpec): ChartSeries[] {
  const raw = (spec as { series?: Array<{ key: string; name?: string; color: string }> }).series
  return (raw ?? []).map((s) => ({ key: s.key, name: s.name || s.key, color: s.color }))
}

/**
 * The categorical field that splits a scatter plot into colored groups, or null
 * if the plot is single-group. Returns `spec.groupBy` when set & valid; otherwise
 * auto-detects: the first row field that is NOT an axis or series key, whose
 * values are all non-empty strings, with ≥2 distinct values. Pure — shared by the
 * renderer and the aria-label builder so they agree.
 */
export function scatterGroupField(spec: ChartSpec): string | null {
  if (spec.type !== 'scatter') return null
  const rows = Array.isArray(spec.data) ? spec.data : []
  if (rows.length === 0) return null

  const allStrings = (key: string) =>
    rows.every((r) => typeof r[key] === 'string' && (r[key] as string).length > 0)
  const distinctCount = (key: string) => new Set(rows.map((r) => r[key] as string)).size

  // An explicit groupBy wins (trust the author) as long as it's a usable
  // multi-value string column.
  if (spec.groupBy && allStrings(spec.groupBy) && distinctCount(spec.groupBy) >= 2) {
    return spec.groupBy
  }

  // Auto-detect: a SMALL categorical split where each group holds multiple points
  // (e.g. 12 points in 2 industries) — NOT a per-point label field (e.g. 8 points
  // each tagged store A–H), which would paint every point its own color.
  const maxGroups = Math.min(CHART_GROUP_COLOR_COUNT, Math.floor(rows.length / 2))
  const reserved = new Set(
    [spec.xAxis?.key, spec.yAxis?.key, ...(spec.series ?? []).map((s) => s.key)].filter(
      Boolean,
    ) as string[],
  )
  for (const key of Object.keys(rows[0])) {
    if (reserved.has(key)) continue
    if (allStrings(key)) {
      const d = distinctCount(key)
      if (d >= 2 && d <= maxGroups) return key
    }
  }
  return null
}

/** A point label longer than this would clutter the plot; such a field is left unlabelled. */
const MAX_POINT_LABEL = 12

/**
 * The field that NAMES each point of a scatter plot, or null.
 *
 * Questions ask about specific points: "Each plotted point is labeled with its
 * student letter … Which student most weakens the association?" The letters
 * were in the data (`{"student":"H","hours":5,"score":55}`) but no renderer
 * drew them, so eight anonymous dots made the question unanswerable — on the
 * website and the phone alike (build-7 grid/figures retest, G1).
 *
 * A label field is a column that is not an axis, a series or the colour group,
 * whose values are short non-empty strings, one per point and all different.
 * scatterGroupField deliberately refuses exactly this kind of column (it would
 * paint every point its own colour); this is where it goes instead. Pure —
 * shared by every renderer and the aria-label builder so they agree.
 */
export function scatterPointLabelField(spec: ChartSpec): string | null {
  if (spec.type !== 'scatter') return null
  const rows = Array.isArray(spec.data) ? spec.data : []
  if (rows.length < 2) return null
  const reserved = new Set(
    [
      spec.xAxis?.key,
      spec.yAxis?.key,
      scatterGroupField(spec),
      ...(spec.series ?? []).map((s) => s.key),
    ].filter(Boolean) as string[],
  )
  for (const key of Object.keys(rows[0])) {
    if (reserved.has(key)) continue
    const values = rows.map((r) => r[key])
    if (
      values.every(
        (v) => typeof v === 'string' && v.trim() !== '' && v.length <= MAX_POINT_LABEL,
      ) &&
      new Set(values).size === rows.length
    ) {
      return key
    }
  }
  return null
}

/** Number of distinct group colors available (keeps auto-detect's cap in sync). */
const CHART_GROUP_COLOR_COUNT = 6

/** Distinct group values in first-seen order (for stable legend/color order). */
export function scatterGroupValues(spec: ChartSpec, field: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const r of spec.data ?? []) {
    const v = r[field]
    if (typeof v === 'string' && !seen.has(v)) {
      seen.add(v)
      out.push(v)
    }
  }
  return out
}

const CHART_BLOCK_RE = /```chart\n([\s\S]*?)```/

/** Raw JSON body of the first ```chart block in a stem, or null if none. */
export function extractChartBlock(stem: string): string | null {
  const m = (stem || '').match(CHART_BLOCK_RE)
  return m ? m[1].trim() : null
}

/** True if the stem embeds a ```chart block. */
export function hasChartBlock(stem: string): boolean {
  return extractChartBlock(stem) !== null
}

/**
 * Repair a stem whose ```chart fences were stripped by an export/copy step
 * (ChatGPT output often loses the backticks). If the stem has a bare `chart`
 * line immediately followed by a JSON object that validates as a chart spec,
 * wrap it back into a ```chart fenced block. Idempotent and conservative: a
 * stem that already contains ```chart — or whose candidate body isn't a valid
 * chart spec — is returned unchanged.
 */
export function normalizeChartFences(stem: string): string {
  const s = stem || ''
  if (s.includes('```chart')) return s
  return s.replace(/\n+[ \t]*chart[ \t]*\n(\{[\s\S]*\})\n+/, (full, body: string) =>
    validateChartSpec(body).ok ? `\n\n\`\`\`chart\n${body.trim()}\n\`\`\`\n\n` : full,
  )
}

export type ChartValidation = { ok: true; spec: ChartSpec } | { ok: false; error: string }

function isAxis(a: unknown): a is { label: string; key: string } {
  if (!a || typeof a !== 'object') return false
  const r = a as Record<string, unknown>
  return typeof r.label === 'string' && typeof r.key === 'string' && r.key !== ''
}

/**
 * Validate a chart-spec JSON string into a renderable ChartSpec. Mirrors
 * exactly what `QuestionChart` needs: a known `type`, axes with non-empty
 * keys, a non-empty `data` array of objects, a non-empty `series`, and —
 * critically — every axis/series key present in each data row (so the
 * rendered chart isn't blank).
 */
export function validateChartSpec(raw: string): ChartValidation {
  const trimmed = (raw || '').trim()
  if (!trimmed) return { ok: false, error: 'empty chart spec' }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (err) {
    return { ok: false, error: `invalid JSON: ${err instanceof Error ? err.message : String(err)}` }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return { ok: false, error: 'chart spec must be a JSON object' }
  const o = parsed as Record<string, unknown>

  if (typeof o.type !== 'string' || !(CHART_TYPES as readonly string[]).includes(o.type))
    return { ok: false, error: `type must be one of ${CHART_TYPES.join(', ')}` }

  if (!isAxis(o.xAxis))
    return { ok: false, error: 'xAxis must have a string label and non-empty key' }
  if (!isAxis(o.yAxis))
    return { ok: false, error: 'yAxis must have a string label and non-empty key' }

  if (!Array.isArray(o.data) || o.data.length === 0)
    return { ok: false, error: 'data must be a non-empty array' }
  const rows = o.data
  if (!rows.every((r) => !!r && typeof r === 'object' && !Array.isArray(r)))
    return { ok: false, error: 'each data row must be an object' }
  const dataRows = rows as Record<string, unknown>[]

  if (!Array.isArray(o.series) || o.series.length === 0)
    return { ok: false, error: 'series must be a non-empty array' }
  const seriesKeys: string[] = []
  for (const s of o.series) {
    if (!s || typeof s !== 'object' || Array.isArray(s))
      return { ok: false, error: 'each series must be an object' }
    const sr = s as Record<string, unknown>
    if (typeof sr.key !== 'string' || sr.key === '')
      return { ok: false, error: 'each series needs a non-empty string key' }
    if (typeof sr.name !== 'string' || typeof sr.color !== 'string')
      return { ok: false, error: `series "${sr.key}" needs a string name and color` }
    seriesKeys.push(sr.key)
  }

  // Key consistency: the x key and every series key must exist in every row,
  // or recharts renders an empty/partial chart.
  for (const k of [o.xAxis.key, ...seriesKeys]) {
    if (!dataRows.every((r) => k in r))
      return { ok: false, error: `data rows are missing key "${k}"` }
  }

  return { ok: true, spec: parsed as ChartSpec }
}
